import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { Box, Button, Flex, Heading, Text, Theme } from "@radix-ui/themes";
import toast from "react-hot-toast";
import PaginatedFileGrid from "@/components/files/PaginatedFileGrid";
import FileUploadZone from "@/components/files/FileUploadZone";
import { fileService } from "@/services/breeze/FileService";
import { File } from "@/types/breeze/File";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (file: File) => void;
  restrictedType?: "image" | "document";
  title?: string;
  description?: string;
  allowUpload?: boolean;
}

export function FilePickerDialog({
  open,
  onOpenChange,
  onSelect,
  restrictedType,
  title = "Pick a file",
  description = "Select a file from your storage to embed.",
  allowUpload = true,
}: Props) {
  const [selected, setSelected] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const acceptFor = restrictedType === "image"
    ? "image/*"
    : restrictedType === "document"
      ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
      : "*/*";

  const handleConfirm = () => {
    if (selected.length > 0) {
      onSelect(selected[0]);
      setSelected([]);
      onOpenChange(false);
    }
  };

  // After upload, auto-select the newly created file so a single click on
  // "Select" completes the flow.
  const handleUpload = async (files: globalThis.File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const created: File[] = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const result = await fileService.create(fd, true);
        if (result && typeof result === "object" && "id" in result) {
          created.push(result as unknown as File);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["files"] });
      if (created.length > 0) setSelected([created[created.length - 1]]);
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}.`);
    } catch (err) {
      console.error("Upload failed", err);
      toast.error("Could not upload file. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) setSelected([]);
        onOpenChange(o);
      }}
    >
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(2px)",
              zIndex: 11000,
            }}
          />
          <Dialog.Content
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "900px",
              width: "calc(100vw - 4rem)",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              padding: "1rem",
              backgroundColor: "var(--color-panel-solid)",
              borderRadius: "var(--radius-4)",
              boxShadow: "var(--shadow-5)",
              zIndex: 11000,
            }}
          >
            <Dialog.Title asChild>
              <Heading size="4" mb="1">{title}</Heading>
            </Dialog.Title>
            <Dialog.Description asChild>
              <Text size="2" color="gray" mb="3">{description}</Text>
            </Dialog.Description>

            {allowUpload && (
              <Box mb="3">
                <FileUploadZone
                  onFileChange={handleUpload}
                  accept={acceptFor}
                  multiple={false}
                  loading={uploading}
                  maxSize={10}
                >
                  <Flex direction="column" align="center" justify="center" style={{ padding: "0.75rem", textAlign: "center" }}>
                    <Text size="2" color="gray" mb="1">
                      {uploading ? (
                        <Text weight="medium">Uploading…</Text>
                      ) : (
                        <>
                          <Text weight="medium">Drop a {restrictedType ?? "file"} here</Text> or click to upload
                        </>
                      )}
                    </Text>
                    <Text size="1" color="gray">
                      Uploaded files are public and immediately pickable.
                    </Text>
                  </Flex>
                </FileUploadZone>
              </Box>
            )}

            <Box style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              <PaginatedFileGrid
                enableSelection
                multiple={false}
                maxFiles={1}
                selectedFiles={selected}
                onSelectionChanged={setSelected}
                compact
                showActions={false}
                restrictedType={restrictedType}
              />
            </Box>

            <Flex gap="2" mt="3" justify="end">
              <Dialog.Close asChild>
                <Button variant="soft" color="gray" type="button">Cancel</Button>
              </Dialog.Close>
              <Button onClick={handleConfirm} disabled={selected.length === 0} type="button">
                Select
              </Button>
            </Flex>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
